import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  ApiActivityGroupResponse,
  ApiTransitionElementResponse,
  ApiTransitionStoryGroupResponse,
} from '../../../api/core/models';
import { SearchIndex } from '../../../feature/dashboard-preview/services/search-index-builder.service';
import { getNodes as getNodesFromBoard } from '../../../feature/dashboard/functions/get-nodes';
import { toIdDict } from '../../../feature/dashboard/functions/to-id-dict';
import { DashboardAdditionalData } from '../../../feature/dashboard/model/dashboard-additional-data';
import { getBaselineInventoryValues, RawBoardData } from '../../../feature/dashboard/raw-board-data';
import { CustomModelDataService } from '../../../feature/dashboard/services/custom-model-data/custom-model-data.service';
import { DashboardV4Service } from '../../../feature/dashboard/services/dashboard-v4/dashboard-v4.service';
import { DashboardV5Service } from '../../../feature/dashboard/services/dashboard-v5/dashboard-v5.service';
import { hasSelectedTE } from '../../../functions/client-model/client-node/has-selected-te';
import { holdsActiveCustomModel } from '../../../functions/client-model/client-node/holds-active-custom-model';
import { filterOnSwimlane } from '../../../functions/filter-on-swimlane';
import { BoardSettings } from '../../../model/board-settings';
import { ClientAction } from '../../../model/client-model/client-action';
import { ClientBoard } from '../../../model/client-model/client-board';
import { ClientIndicator } from '../../../model/client-model/client-indicator';
import { ClientNode } from '../../../model/client-model/client-node';
import { CustomTargetModelData } from '../../../model/custom-model/custom-target-model-data';
import { Dict } from '../../../model/dict';
import { Language } from '../../../model/enums/language';
import { createNodesAndIndicators } from '../../../public/dashboard/shared/functions/dashboard-mappers/create-nodes-and-indicators';
import { getChildren } from '../../../public/dashboard/shared/functions/get-children';
import { ClientBoardContainer } from '../../../public/dashboard/shared/model/client-board-container';
import { DashboardV4 } from '../../../public/dashboard/shared/model/dashboard-v4';
import {
  DashboardV5CoreModule,
  DashboardV5EmissionsModule,
  DashboardV5IndicatorsModule,
  DashboardV5InterventionsModule,
  DashboardV5TransitionsModule,
} from '../../../public/dashboard/store/dashboard-v5';
import { AppConfigService } from '../../../services/app-config.service';
import { ModelService } from '../../../services/backend-service/model.service';
import { PublishedBoardsModelService } from '../../../services/backend-service/published-boards-model.service';
import {
  generateModelTranslationKey,
  ModelTranslationOutputType,
} from '../../../services/climate-engine/ccc-model-utility/generate-model-translation-key';
import { PublishedBoardsFacadeService } from '../../../services/store/published-boards/published-boards-facade.service';
import { TranslationService } from '../../../services/translation-service';
import { InventoriesWithValues, InventoryWithValues } from '../../../store/inventories/inventories.types';
import { PublishedBoardsModule } from '../../../store/published-boards/published-boards-modules';

@Injectable({
  providedIn: 'root',
})
export class PublishedBoardBuilderService {
  config = inject(AppConfigService);
  dashboardV4Service = inject(DashboardV4Service);
  clientModelService = inject(ModelService);
  customModelDataService = inject(CustomModelDataService);
  publishedBoardsFacade = inject(PublishedBoardsFacadeService);
  publishedBoardsModelService = inject(PublishedBoardsModelService);
  translationService = inject(TranslationService);

  private _dashboardV5Service = inject(DashboardV5Service);

  buildDashboardV5CoreModule(boardId: string): Observable<DashboardV5CoreModule> {
    return this.publishedBoardsFacade
      .getPublishedBoardModule$<PublishedBoardsModule.CORE>(boardId, PublishedBoardsModule.CORE)
      .pipe(
        switchMap((coreData) => {
          // <any> since we've introduced client interfaces for published boards instead of using api generated models
          const board = this.publishedBoardsModelService.publishedV4_1BoardToClientBoard(<any>coreData) as ClientBoard;
          const language = coreData.primaryLanguage! as Language;

          return this.translationService.loadLanguageIfNeeded(language, 'dashboard').pipe(
            map(() =>
              this._dashboardV5Service.toDashboardCoreModule(<ClientBoardContainer>{
                ...board,
                boardSettings: board.boardSettings,
                id: board.id,
                inventories: new Map<string, InventoryWithValues>(),
              })
            )
          );
        }),
        catchError((err) => {
          console.log(err);
          return of({} as DashboardV5CoreModule);
        })
      );
  }

  buildDashboardV5EmissionsModule(board: DashboardV4, isPanorama: boolean): Observable<DashboardV5EmissionsModule> {
    return forkJoin([
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.EMISSIONS>(
        board.id,
        PublishedBoardsModule.EMISSIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.CALCULATIONS>(
        board.id,
        PublishedBoardsModule.CALCULATIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.TRANSITIONS>(
        board.id,
        PublishedBoardsModule.TRANSITIONS
      ),
    ]).pipe(
      map(([emissionsData, calculationData, transitionData]) => {
        const language = board.boardSettings.internationalisation.defaultLanguage;
        if (transitionData?.supplyElements?.length) {
          board.supplyChildNodes.length = 0;
          transitionData.supplyElements.forEach(({ id }: { id: string }) => board.supplyChildNodes.push(id));
        }
        if (board.childNodes.length === 0) {
          board.childNodes = transitionData.childNodes;
        }
        const clientBoardContainer = createBoardContainerV4(
          <any>{
            board,
            clientActions: [],
            clientIndicators: [],
            clientInventories: new Map<string, InventoryWithValues>(),
            clientNodes: this.clientModelService.toClientNodes(<any>Object.values(transitionData.nodes ?? {})),
          },
          this.customModelDataService,
          isPanorama
        );
        setTETitles(getNodesFromBoard(clientBoardContainer), language, this.translationService);

        return this._dashboardV5Service.toDashboardEmissionsModule(
          { ...calculationData, activityTree: emissionsData.activityGroups } as DashboardAdditionalData,
          clientBoardContainer,
          isPanorama
        );
      }),
      catchError((err) => {
        console.log(err);
        return of({} as DashboardV5EmissionsModule);
      })
    );
  }

  buildDashboardV5IndicatorsModule(board: DashboardV4, isPanorama: boolean): Observable<DashboardV5IndicatorsModule> {
    return forkJoin([
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.CALCULATIONS>(
        board.id,
        PublishedBoardsModule.CALCULATIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.TRANSITIONS>(
        board.id,
        PublishedBoardsModule.TRANSITIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.INTERVENTIONS>(
        board.id,
        PublishedBoardsModule.INTERVENTIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.EMISSIONS>(
        board.id,
        PublishedBoardsModule.EMISSIONS
      ),
    ]).pipe(
      map(([calculationData, transitionsData, interventionData, emissionsData]) => {
        const language = board.boardSettings.internationalisation.defaultLanguage;
        if (transitionsData?.supplyElements?.length) {
          board.supplyChildNodes.length = 0;
          transitionsData.supplyElements.forEach(({ id }: { id: string }) => board.supplyChildNodes.push(id));
        }
        if (board.childNodes.length === 0) {
          board.childNodes = transitionsData.childNodes;
        }
        const clientBoardContainer = createBoardContainerV4(
          <any>{
            board,
            clientActions: this.clientModelService.toClientActions(
              Object.entries(interventionData ?? {}).map(([id, action]) => ({ ...(action as any), id }))
            ),
            clientIndicators: this.clientModelService.toClientIndicators(
              Object.values(transitionsData?.indicators ?? {})
            ),
            clientInventories: new Map<string, InventoryWithValues>(),
            clientNodes: this.clientModelService.toClientNodes(<any>Object.values(transitionsData?.nodes ?? {})),
          },
          this.customModelDataService
        );
        setTETitles(getNodesFromBoard(clientBoardContainer), language, this.translationService);

        return this._dashboardV5Service.toDashboardIndicatorsModule(
          { ...calculationData, activityTree: emissionsData.activityGroups },
          clientBoardContainer,
          isPanorama
        );
      })
    );
  }

  buildDashboardV5InterventionsModule(board: DashboardV4): Observable<DashboardV5InterventionsModule> {
    return forkJoin([
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.INTERVENTIONS>(
        board.id,
        PublishedBoardsModule.INTERVENTIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.TRANSITIONS>(
        board.id,
        PublishedBoardsModule.TRANSITIONS
      ),
    ]).pipe(
      map(([interventionData, transitionData]) => {
        const language = board.boardSettings.internationalisation.defaultLanguage;
        if (transitionData?.supplyElements?.length) {
          board.supplyChildNodes.length = 0;
          transitionData.supplyElements.forEach(({ id }: { id: string }) => board.supplyChildNodes.push(id));
        }
        if (board.childNodes.length === 0) {
          board.childNodes = transitionData.childNodes;
        }
        const clientBoardContainer = createBoardContainerV4(
          <any>{
            board,
            clientActions: this.clientModelService.toClientActions(
              Object.entries(interventionData ?? {}).map(([id, action]) => ({ ...(action as any), id }))
            ),
            clientIndicators: [],
            clientInventories: new Map<string, InventoryWithValues>(),
            clientNodes: this.clientModelService.toClientNodes(<any>Object.values(transitionData?.nodes ?? {})),
          },
          this.customModelDataService
        );
        setTETitles(getNodesFromBoard(clientBoardContainer), language, this.translationService);

        return this._dashboardV5Service.toDashboardInterventionsModule(clientBoardContainer);
      }),
      catchError((err) => {
        console.log(err);
        return of({} as DashboardV5InterventionsModule);
      })
    );
  }

  buildDashboardV5SearchModule(boardId: string): Observable<SearchIndex[]> {
    return this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.SEARCH>(
      boardId,
      PublishedBoardsModule.SEARCH
    );
  }

  buildDashboardV5TransitionModule(board: DashboardV4, isPanorama: boolean): Observable<DashboardV5TransitionsModule> {
    return forkJoin([
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.CALCULATIONS>(
        board.id,
        PublishedBoardsModule.CALCULATIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.TRANSITIONS>(
        board.id,
        PublishedBoardsModule.TRANSITIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.INTERVENTIONS>(
        board.id,
        PublishedBoardsModule.INTERVENTIONS
      ),
      this.publishedBoardsFacade.getPublishedBoardModule$<PublishedBoardsModule.EMISSIONS>(
        board.id,
        PublishedBoardsModule.EMISSIONS
      ),
    ]).pipe(
      map(([calculationData, transitionData, interventionData, emissionsData]) => {
        if (transitionData?.supplyElements?.length) {
          board.supplyChildNodes.length = 0;
          transitionData.supplyElements.forEach(({ id }: { id: string }) => board.supplyChildNodes.push(id));
        }
        if (board.childNodes.length === 0) {
          board.childNodes = transitionData.childNodes;
        }
        const language = board.boardSettings.internationalisation.defaultLanguage;
        const clientBoardContainer = createBoardContainerV4(
          <any>{
            board,
            clientActions: this.clientModelService.toClientActions(
              Object.entries(interventionData ?? {}).map(([id, action]) => ({ ...(action as any), id }))
            ),
            clientIndicators: this.clientModelService.toClientIndicators(
              Object.values(transitionData?.indicators ?? {})
            ),
            clientInventories: new Map<string, InventoryWithValues>(),
            clientNodes: this.clientModelService.toClientNodes(<any>Object.values(transitionData?.nodes ?? {})),
          },
          this.customModelDataService,
          isPanorama
        );
        setTETitles(getNodesFromBoard(clientBoardContainer), language, this.translationService);

        return this._dashboardV5Service.toDashboardTransitionsModule(
          { ...calculationData, activityTree: emissionsData.activityGroups },
          clientBoardContainer,
          isPanorama
        );
      }),
      catchError((err) => {
        console.log(err);
        return of({} as DashboardV5TransitionsModule);
      })
    );
  }

  buildDashboardV4(boardId: string, isPanorama: boolean = false): Observable<DashboardV4> {
    const baseUrlFiles = this.config.baseUrlFiles;
    return this.publishedBoardsFacade.getPublishedBoardData$<'V4'>(boardId).pipe(
      switchMap((blobData) => {
        let clientNodes: ClientNode[];
        let clientIndicators: ClientIndicator[];
        const clientActions = this.clientModelService
          .toClientActions(Object.entries(blobData?.actions ?? {}).map(([id, action]) => ({ ...(action as any), id })))
          .filter((a) => !a.actionProperties.isWorkingMaterial);
        // initialising empty inventories, since published dashboards doesn't need that data to render
        const clientInventories: InventoriesWithValues = new Map<string, InventoryWithValues>();

        const isV4 = 'board' in blobData;
        const isV41 = 'data' in blobData;
        const isV42 = 'transitionData' in blobData && blobData.transitionData?.transitionStoryGroups !== null;
        let language: Language = Language.ENGLISH_GB;
        let board: ClientBoard;

        if (isV4) {
          board = this.clientModelService.toClientBoard(blobData?.board ?? {});
          language = (board.boardSettings as BoardSettings).internationalisation.defaultLanguage;
        } else if (isV41) {
          board = this.publishedBoardsModelService.publishedV4_1BoardToClientBoard(blobData.data) as ClientBoard;
          language = blobData.data.primaryLanguage;
        }
        if (isV42) {
          board = this.publishedBoardsModelService.publishedV4_1BoardToClientBoard(blobData.data) as ClientBoard;
          language = blobData.data.primaryLanguage;
          const { nodes, indicators, supplyElements } = createNodesAndIndicators(
            ((blobData.transitionData as any)?.supplyTransitionElements as ApiTransitionElementResponse[]) ?? [],
            blobData.transitionData?.transitionStoryGroups as unknown as ApiTransitionStoryGroupResponse[],
            boardId,
            baseUrlFiles
          );
          board.childNodes = blobData.transitionData.transitionStoryGroups?.map((group) => group.id) ?? [];
          clientNodes = nodes.concat(supplyElements);
          clientIndicators = this.clientModelService.toClientIndicators(indicators);

          if ((blobData.transitionData as any)?.supplyTransitionElements) {
            board.supplyChildNodes.length = 0;
            supplyElements.forEach(({ id }: { id: string }) => board.supplyChildNodes.push(id));
          }
          if ((blobData.transitionData as any)?.childNodes && board.childNodes.length === 0) {
            board.childNodes = (blobData.transitionData as any)?.childNodes;
          }
        } else {
          clientNodes = this.clientModelService.toClientNodes(Object.values(blobData?.nodes ?? {}));
          clientIndicators = this.clientModelService.toClientIndicators(Object.values(blobData?.indicators ?? {}));
        }

        return this.translationService.loadLanguageIfNeeded(language, 'dashboard').pipe(
          map(() => {
            const activityGroups: ApiActivityGroupResponse[] = blobData?.calculations?.activityTree ?? [];
            const clientBoardContainer = createBoardContainerV4(
              { board, clientNodes, clientActions, clientInventories, clientIndicators, activityGroups },
              this.customModelDataService,
              isPanorama
            );

            setTETitles(getNodesFromBoard(clientBoardContainer), language, this.translationService);
            const dashboardV4 = this.dashboardV4Service.toDashboard(
              blobData.calculations as DashboardAdditionalData,
              clientBoardContainer,
              activityGroups,
              false,
              isPanorama
            );
            return dashboardV4 as DashboardV4;
          })
        );
      }),
      catchError((err) => {
        console.log(err);
        return of({} as DashboardV4);
      })
    );
  }
}

function applyGroupColour(board: ClientBoardContainer): void {
  const applyColourIfSet = (node: ClientNode, groupColour?: string) => {
    if (groupColour) {
      node.nodeProperties.groupColour = groupColour;
      getChildren(board, node).forEach((child) => applyColourIfSet(child, groupColour));
    }
  };
  const topLevelNodes = getChildren(board, board);
  topLevelNodes.forEach((node) => applyColourIfSet(node, node?.nodeProperties?.groupColour ?? undefined));
}

function createBoardContainer(data: RawBoardData): ClientBoardContainer {
  //removing any bad references on the board object
  //TODO: Find a better place for this, we filter nodes in the backend-service, but when retrieving the board there we don't have the node list to look at.
  //Perhaps we could add a similar construct as the ClientBoardContainer, i.e. that holds the related objects.
  const filteredActions = filterActions(data.board, data.clientActions);
  const validNodeIds = data.clientNodes.map((n) => n.id);
  const validNodes = data.board.childNodes.filter((n) => validNodeIds.includes(n));
  const board = { ...data.board, childNodes: validNodes };
  const nodes = toIdDict(
    data.clientNodes.map((node) => ({ ...node, childNodes: node.childNodes.filter((n) => validNodeIds.includes(n)) }))
  );
  const actions = toIdDict(filteredActions);
  const indicators = toIdDict(data.clientIndicators);
  const baselineInventory = getBaselineInventoryValues(data.clientInventories, board);
  const parameters = baselineInventory?.variables;
  const boardContainer = { ...board, nodes, actions, indicators, parameters };
  applyGroupColour(boardContainer);
  return boardContainer;
}

function filterActions(board: ClientBoard, actions: ClientAction[]): ClientAction[] {
  // Filter out non public actions and actions with deprecated swimlanes, see TODO in _toDashboard below
  return actions.filter((a) => filterOnSwimlane(a, board)).filter((a) => !a.actionProperties.isWorkingMaterial);
}

//TODO: move to service, this is just copied in here from dashboard.resolver to ensure that the preview and published resolver handle the container in the same way.
function createBoardContainerV4(
  data: RawBoardData,
  customModelDataService: CustomModelDataService,
  isPanorama: boolean = false
): ClientBoardContainer {
  const boardContainer = createBoardContainer(data);
  const nodes = Object.entries(boardContainer.nodes).reduce((dict: Dict<ClientNode>, [nodeId, node]) => {
    if (holdsActiveCustomModel(node)) {
      const customTargetModelData: CustomTargetModelData = customModelDataService.createValidChartDataV4(
        node?.nodeProperties?.customTargetModelData,
        boardContainer,
        boardContainer.boardSettings.timeline,
        isPanorama
      );
      node = { ...node, nodeProperties: { ...node.nodeProperties, customTargetModelData } };
    }
    dict[nodeId] = node;
    return dict;
  }, {});
  return { ...boardContainer, nodes };
}

function setTETitles(nodes: ClientNode[], lang: Language, translationService: TranslationService): void {
  nodes.forEach((node) => {
    if (hasSelectedTE(node)) {
      const key = generateModelTranslationKey(
        ModelTranslationOutputType.TransitionElementTitle,
        node.nodeProperties.transitionElement
      );
      node.title = translationService.translate(lang, key);
    }
  });
}
